import React from 'react';
import { X } from 'lucide-react';

export const StudentDetailsModal = ({ student, onClose }) => {
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 relative">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            Student Details
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2">Basic Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">Name</label>
                <p className="text-sm text-slate-900">{student.firstName} {student.lastName}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">ID</label>
                <p className="text-sm text-slate-900">{student.id}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Email</label>
                <p className="text-sm text-slate-900">{student.email || 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Phone</label>
                <p className="text-sm text-slate-900">{student.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Military Information */}
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2">Military Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">Branch</label>
                <p className="text-sm text-slate-900">{student.militaryBranch}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Affiliation</label>
                <p className="text-sm text-slate-900">{student.militaryAffiliation}</p>
              </div>
            </div>
          </div>

          {/* Music Information */}
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2">Music Information</h4>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-500">Desired Instruments</label>
                <p className="text-sm text-slate-900">{student.desiredInstruments || 'None specified'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Previous Experience</label>
                <p className="text-sm text-slate-900 whitespace-pre-line">
                  {student.previousExperience || 'No previous experience'}
                </p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Goals</label>
                <p className="text-sm text-slate-900 whitespace-pre-line">
                  {student.goals || 'No goals specified'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-800 focus:outline-none"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook for fetching student details
export const useStudentDetails = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);

  const fetchDetails = async (studentId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/${studentId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch student details');
      }

      const data = await response.json();
      setStudentDetails(data);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { fetchDetails, isLoading, error, studentDetails };
};